import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { StaffProfile } from '../models/staffProfile';
import { UserDataService } from '../services/userDataService';
import { useAppState } from '../context/AppStateContext';
import { signOutAndRedirect } from '../api/axios';
import PickerSignOutButton from '../components/common/PickerSignOutButton';
import PinKeypad from '../components/PinKeypad';
import { useHelpOptional } from '../help/HelpProvider';
import HelpAsk from '../help/HelpAsk';
import PasswordReauthModal from '../components/PasswordReauthModal';
import rustleLogo from '../images/rustle_logo2.png';
import { clearSelectedOrgId } from '../services/selectedOrg';

const StaffSelect: React.FC = () => {
  const navigate = useNavigate();
  const { setActiveUser, setActiveUserToken, setIdleTimeoutMs, setIsLocked } = useAppState();
  // This screen has no left menu, so it has no help button — and nobody finds
  // F1 on their own. The PIN How Tos are the ones people need here.
  const help = useHelpOptional();

  const [staff, setStaff] = useState<StaffProfile[]>([]);
  const [orgName, setOrgName] = useState<string>('');
  const [orgLogoUrl, setOrgLogoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStaff, setSelectedStaff] = useState<StaffProfile | null>(null);
  const [setupPromptStaff, setSetupPromptStaff] = useState<StaffProfile | null>(null);
  const [pinError, setPinError] = useState<string | null>(null);
  // Set when the user clicks "Forgot your PIN?" → drives the Cognito re-auth modal.
  const [forgetPinTarget, setForgetPinTarget] = useState<StaffProfile | null>(null);

  const loadStaff = async () => {
    setLoading(true);
    setError(null);
    try {
      const [profiles, org] = await Promise.all([
        // pickerOnly → server filters to eligible switch-user candidates:
        // the requester themself, plus other non-external Editors (when
        // the requester is internal). Externals see only themself.
        UserDataService.fetchStaffProfiles(true),
        UserDataService.fetchOrgSettings(),
      ]);
      setStaff(profiles);
      setOrgName(org.name);
      setOrgLogoUrl(org.logoUrl);
      setIdleTimeoutMs(org.inactivityTimeoutSeconds * 1000);
    } catch {
      setError('Failed to load staff profiles. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Clear any existing active user token when this screen opens. Also drop the
    // persisted lock flag — we're starting a fresh user selection, so a stale
    // "locked" state from before the refresh must not follow us back into the app.
    sessionStorage.removeItem('activeUserToken');
    setActiveUser(null);
    setActiveUserToken(null);
    setIsLocked(false);
    loadStaff();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePinSubmit = async (pin: string) => {
    if (!selectedStaff) return;
    const result = await UserDataService.verifyPin(selectedStaff.id, pin);
    sessionStorage.setItem('activeUserToken', result.activeUserToken);
    setActiveUserToken(result.activeUserToken);
    setActiveUser(result.activeUser);
    navigate('/');
  };

  const handleSelectStaff = (profile: StaffProfile) => {
    setPinError(null);
    if (!profile.hasPin) {
      // Route no-PIN staff to a setup-prompt dialog rather than the PIN keypad.
      setSetupPromptStaff(profile);
      return;
    }
    setSelectedStaff(profile);
  };

  if (loading) {
    return (
      <div className="rustle-background d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div style={{ position: 'relative', zIndex: 1 }} className="d-flex flex-column align-items-center">
          <div className="loading-spinner mb-3" />
          <p className="text-muted">Loading staff…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rustle-background d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <PickerSignOutButton />
        <div style={{ position: 'relative', zIndex: 1 }} className="d-flex flex-column align-items-center gap-3">
          <img src={rustleLogo} alt="Rustle" style={{ maxWidth: 120, marginBottom: '1rem' }} />
          <div className="alert alert-danger mb-0" style={{ maxWidth: 420 }}>{error}</div>
          <div className="d-flex gap-2">
            <button className="btn btn-primary" onClick={loadStaff}>Try again</button>
            <button className="btn btn-outline-secondary" onClick={() => navigate('/select-org')}>Change workspace</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rustle-background" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <PickerSignOutButton />
      <div className="picker-shell">
        {orgLogoUrl ? (
          <img
            src={orgLogoUrl}
            alt={orgName}
            style={{
              maxWidth: 320,
              maxHeight: 260,
              width: 'auto',
              height: 'auto',
              marginBottom: '2.5rem',
            }}
          />
        ) : (
          <div
            style={{
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: 'linear-gradient(160deg, #26923f 0%, #106123 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              boxShadow: '1px 6px 18px rgba(34,139,34,0.3)',
              marginBottom: '1.25rem',
              border: '3px solid rgba(255,255,255,0.5)',
            }}
          >
            <span style={{ color: '#fff', fontSize: '2.5rem', fontWeight: 700, userSelect: 'none' }}>
              {orgName.charAt(0).toUpperCase() || '?'}
            </span>
          </div>
        )}

        {/* Matches the workspace picker's legend, and deliberately echoes its
            verb: the two screens are one flow, so "Choose where to work" is
            followed by "Choose your profile" rather than a fresh phrasing. */}
        <div className="picker-heading">
          <h1 className="picker-heading__title">Choose your profile</h1>
        </div>

        {/* Sibling of the workspace picker — same tile language, same grid. */}
        <div className="picker-grid" data-help="account.profile-tiles">
          {staff.map((profile) => (
            <button
              key={profile.id}
              type="button"
              onClick={() => handleSelectStaff(profile)}
              className="child-card picker-card"
              title={profile.hasPin ? `Sign in as ${profile.name}` : `${profile.name} — set up your PIN`}
            >
              <div
                className="picker-card__avatar"
                style={{ background: profile.colourCode }}
                aria-hidden="true"
              >
                {profile.initials}
              </div>
              <div className="child-card__body">
                <div className="child-card__name">{profile.name}</div>
              </div>
              {!profile.hasPin && (
                <div className="picker-card__tags">
                  <span className="picker-card__tag picker-card__tag--todo">Set up a PIN</span>
                </div>
              )}
            </button>
          ))}
        </div>

        {/* In the app proper this lives at the foot of the left menu. There is
            no menu here, and this page is a single centred column, so the link
            sits in the flow under the tiles rather than floating in a corner
            of an otherwise empty screen. */}
        {help && (
          <button
            type="button"
            className="picker-help"
            onClick={help.toggleHelp}
            aria-expanded={help.open}
          >
            <i className="fas fa-circle-question" aria-hidden="true" />
            Help with PINs and profiles
          </button>
        )}
      </div>

      {selectedStaff && (
        <PinKeypad
          staffName={selectedStaff.name}
          staffInitials={selectedStaff.initials}
          staffColour={selectedStaff.colourCode}
          onSubmit={handlePinSubmit}
          onCancel={() => { setSelectedStaff(null); setPinError(null); }}
          dismissOnBackdrop
          onLockout={async () => {
            await signOutAndRedirect();
          }}
          onForgetPin={selectedStaff.hasPin ? async () => {
            // PinKeypad's confirmation overlay calls this. The actual nullification
            // is gated by a Cognito re-auth modal below — this just opens it.
            setForgetPinTarget(selectedStaff);
          } : undefined}
          error={pinError}
        />
      )}

      {setupPromptStaff && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(15,15,25,0.6)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setSetupPromptStaff(null)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 24,
              width: 360,
              maxWidth: 'calc(100vw - 2rem)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
              overflow: 'hidden',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div
              style={{
                background: `linear-gradient(135deg, ${setupPromptStaff.colourCode}22 0%, ${setupPromptStaff.colourCode}08 100%)`,
                borderBottom: '1px solid rgba(0,0,0,0.06)',
                padding: '1.5rem 1.25rem 1.25rem',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  width: 68, height: 68, borderRadius: '50%',
                  background: setupPromptStaff.colourCode,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 0.75rem',
                  fontSize: '1.4rem', fontWeight: 700, color: '#fff',
                  boxShadow: `0 4px 16px ${setupPromptStaff.colourCode}55`,
                }}
              >
                {setupPromptStaff.initials}
              </div>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: '#222', marginBottom: 2 }}>
                Hi, {setupPromptStaff.name.split(' ')[0]}
              </div>
              <div
                style={{
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: 'rgba(13, 77, 26, 0.55)',
                  marginTop: 4,
                }}
              >
                PIN setup needed
              </div>
            </div>

            <div style={{ padding: '1.25rem 1.5rem 1.5rem' }}>
              <p style={{ fontSize: '0.9rem', color: '#555', lineHeight: 1.5, textAlign: 'center', margin: 0 }}>
                You haven&apos;t set a PIN yet. Sign in with your email to create one.
              </p>

              {/* The guide belongs here rather than in the help list, because
                  this is the only place that knows the answer to "do you need
                  it?" — hasPin, per profile. Listed on the picker it was
                  offered to everyone, most of whom set their PIN long ago. */}
              <div style={{ textAlign: 'center', marginTop: '0.85rem' }}>
                <HelpAsk guideKey="account.set-up-pin" label="What happens next?" variant="inline" />
              </div>

              <button
                onClick={async () => {
                  setSetupPromptStaff(null);
                  await signOutAndRedirect();
                }}
                style={{
                  width: '100%',
                  marginTop: '1.25rem',
                  padding: '10px 14px',
                  background: 'linear-gradient(160deg, #26923f 0%, #106123 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 12,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(38, 146, 63, 0.35)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                <i className="fas fa-right-from-bracket" style={{ fontSize: '0.82rem' }} />
                Sign out to set up PIN
              </button>

              <button
                onClick={() => setSetupPromptStaff(null)}
                style={{
                  width: '100%',
                  marginTop: '0.4rem',
                  padding: '8px',
                  background: 'transparent',
                  color: '#888',
                  border: 'none',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {forgetPinTarget && (
        <PasswordReauthModal
          defaultEmail={forgetPinTarget.email}
          title={`Reset ${forgetPinTarget.name}'s PIN`}
          // Not "sign in as": they already are signed in, and the button below
          // signs them OUT. This asks them to confirm who they are.
          description={`Confirm the email address and password for ${forgetPinTarget.name} to clear their PIN. The device will then sign out, and the next sign-in will ask for a new PIN.`}
          // One screen now, so this button carries the whole act: it proves who
          // you are AND clears the PIN AND signs the device out. The label says
          // all three, and the colour matches, because this is the click that
          // cannot be taken back.
          confirmLabel="Reset PIN & sign out"
          confirmVariant="danger"
          onCancel={() => setForgetPinTarget(null)}
          onConfirmed={async () => {
            const target = forgetPinTarget;
            setForgetPinTarget(null);
            try {
              // Auth.signIn just rotated the Cognito session to this user — the server
              // will now see them as the device-session user, satisfying the same-user check.
              await UserDataService.forgetMyPin(target.id);
            } finally {
              sessionStorage.removeItem('activeUserToken');
              clearSelectedOrgId({ forgetLastUsed: true });
              setActiveUser(null);
              setActiveUserToken(null);
              await Auth.signOut();
              navigate('/login');
            }
          }}
        />
      )}
      
    </div>
  );
};

export default StaffSelect;
